/**
 * Плагин сериализации для CSElement
 * Поддерживает различные форматы сериализации
 */

import { CSElementPlugin, LifecycleHooks, PluginOptions } from '../types/plugin-interfaces';
import { ICSElement } from '../types/interfaces';
import { CSElement } from '../core/CSElement';
import * as pako from 'pako';
import * as CryptoJS from 'crypto-js';
import * as yaml from 'js-yaml';
import * as msgpack from 'msgpack-lite';

/**
 * Поддерживаемые форматы сериализации
 */
export enum SerializationFormat {
  JSON = 'json',
  XML = 'xml',
  YAML = 'yaml',
  CSV = 'csv',
  BINARY = 'binary',
  CUSTOM = 'custom'
}

/**
 * Опции сериализации
 */
export interface SerializationOptions {
  format: SerializationFormat;
  includeMetadata?: boolean;
  includeChildren?: boolean;
  includeParent?: boolean;
  pretty?: boolean;
  compression?: boolean;
  encryption?: boolean;
  customOptions?: Record<string, any>;
}

/**
 * Результат сериализации
 */
export interface SerializationResult {
  success: boolean;
  data: string | Buffer;
  format: SerializationFormat;
  size: number;
  metadata?: Record<string, any>;
  error?: string;
}

/**
 * Результат десериализации
 */
export interface DeserializationResult {
  success: boolean;
  element?: any;
  metadata?: Record<string, any>;
  error?: string;
}

/**
 * Сериализатор для конкретного формата
 */
export interface FormatSerializer {
  format: SerializationFormat;
  serialize(element: ICSElement, options: SerializationOptions): Promise<string | Buffer>;
  deserialize(data: string | Buffer, options: SerializationOptions): Promise<any>;
  validate?(data: string | Buffer): boolean;
  [key: string]: any; // Разрешаем дополнительные методы
}

/**
 * Конфигурация сериализации
 */
export interface SerializationConfig {
  enabled: boolean;
  defaultFormat: SerializationFormat;
  enabledFormats: SerializationFormat[];
  enableCache: boolean;
  enableCompression: boolean;
  enableEncryption: boolean;
  maxCacheSize: number;
  compressionLevel: number;
  encryptionKey?: string;
}

/**
 * Плагин сериализации
 */
export class SerializationPlugin implements CSElementPlugin {
  public readonly name = 'SerializationPlugin';
  public readonly version = '1.0.0';
  public readonly description = 'Плагин для сериализации данных в разные форматы';

  private config: SerializationConfig = {
    enabled: true,
    defaultFormat: SerializationFormat.JSON,
    enabledFormats: [
      SerializationFormat.JSON,
      SerializationFormat.XML,
      SerializationFormat.YAML,
      SerializationFormat.CSV,
      SerializationFormat.BINARY
    ],
    enableCache: true,
    enableCompression: false,
    enableEncryption: false,
    maxCacheSize: 1000,
    compressionLevel: 6
  };

  private serializers = new Map<SerializationFormat, FormatSerializer>();
  private cache = new Map<string, SerializationResult>();

  constructor(options?: PluginOptions) {
    if (options?.config) {
      this.config = { ...this.config, ...options.config };
    }

    this.initializeSerializers();
  }

  install(CSElementClass: typeof CSElement): void {
    console.log(`SerializationPlugin установлен для ${CSElementClass.name}`);
  }

  getLifecycleHooks(): LifecycleHooks {
    return {};
  }

  /**
   * Сериализация элемента
   */
  async serialize(
    element: ICSElement,
    format: SerializationFormat
  ): Promise<SerializationResult> {
    if (!this.config.enabled) {
      return {
        success: false,
        data: '',
        format,
        size: 0,
        error: 'Сериализация отключена'
      };
    }

    const fullOptions: SerializationOptions = {
      format,
      includeMetadata: true,
      includeChildren: true,
      includeParent: false,
      pretty: false,
      compression: this.config.enableCompression,
      encryption: this.config.enableEncryption,
    };

    // Проверяем кэш
    const cacheKey = this.generateCacheKey(element, fullOptions);
    if (this.config.enableCache && this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    try {
      const serializer = this.serializers.get(format);
      if (!serializer) {
        return {
          success: false,
          data: '',
          format,
          size: 0,
          error: `Сериализатор для формата ${format} не найден`
        };
      }

      let data = await serializer.serialize(element, fullOptions);

      // Применяем сжатие если включено
      if (fullOptions.compression) {
        data = await this.compress(data);
      }

      // Применяем шифрование если включено
      if (fullOptions.encryption && this.config.encryptionKey) {
        data = await this.encrypt(data);
      }

      const result: SerializationResult = {
        success: true,
        data,
        format,
        size: typeof data === 'string' ? data.length : data.byteLength,
        metadata: {
          timestamp: Date.now(),
          compressed: fullOptions.compression,
          encrypted: fullOptions.encryption,
          options: fullOptions
        }
      };

      // Кэшируем результат
      if (this.config.enableCache) {
        this.cacheResult(cacheKey, result);
      }

      return result;

    } catch (error) {
      return {
        success: false,
        data: '',
        format,
        size: 0,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  /**
   * Десериализация данных
   */
  async deserialize(
    data: string,
    format: SerializationFormat
  ): Promise<DeserializationResult> {
    if (!this.config.enabled) {
      return {
        success: false,
        error: 'Сериализация отключена'
      };
    }

    const fullOptions: SerializationOptions = {
      format,
      includeMetadata: true,
      includeChildren: true,
      includeParent: false,
      compression: this.config.enableCompression,
      encryption: this.config.enableEncryption,
    };

    try {
      let processedData = data;

      // Расшифровываем если нужно
      if (fullOptions.encryption && this.config.encryptionKey) {
        const decrypted = await this.decrypt(processedData);
        processedData = typeof decrypted === 'string' ? decrypted : decrypted.toString();
      }

      // Распаковываем если нужно
      if (fullOptions.compression) {
        const decompressed = await this.decompress(processedData);
        processedData = typeof decompressed === 'string' ? decompressed : decompressed.toString();
      }



      const serializer = this.serializers.get(format);
      if (!serializer) {
        return {
          success: false,
          error: `Сериализатор для формата ${format} не найден`
        };
      }

      const element = await serializer.deserialize(processedData, fullOptions);

      return {
        success: true,
        element,
        metadata: {
          timestamp: Date.now(),
          format,
          options: fullOptions
        }
      };

    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  /**
   * Регистрация пользовательского сериализатора
   */
  registerSerializer(serializer: FormatSerializer): void {
    this.serializers.set(serializer.format, serializer);
    
    if (!this.config.enabledFormats.includes(serializer.format)) {
      this.config.enabledFormats.push(serializer.format);
    }
  }

  /**
   * Удаление сериализатора
   */
  unregisterSerializer(format: SerializationFormat): boolean {
    return this.serializers.delete(format);
  }

  /**
   * Инициализация встроенных сериализаторов
   */
  private initializeSerializers(): void {
    // JSON сериализатор
    this.serializers.set(SerializationFormat.JSON, {
      format: SerializationFormat.JSON,
      async serialize(element: ICSElement, options: SerializationOptions): Promise<string> {
        const data = this.elementToObject(element, options);
        return options.pretty ? JSON.stringify(data, null, 2) : JSON.stringify(data);
      },
      async deserialize(data: string | Buffer): Promise<any> {
        return JSON.parse(data.toString());
      },
      validate(data: string | Buffer): boolean {
        try {
          JSON.parse(data.toString());
          return true;
        } catch {
          return false;
        }
      },
      elementToObject(element: ICSElement, options: SerializationOptions): any {
        // Используем встроенную сериализацию CSElement
        if (typeof (element as any).serialize === 'function') {
          return (element as any).serialize({
            includeChildren: options.includeChildren,
            includeData: true,
            includeMetadata: options.includeMetadata
          });
        }

        // Fallback для совместимости
        const result: any = {
          id: element.id,
          name: element.name,
          data: Array.from(element.data.entries()).reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
          }, {} as Record<string, any>)
        };

        if (options.includeMetadata) {
          result.metadata = {
            index: element.index,
            elementsCount: element.elementsCount(),
            ownersCount: element.ownersCount()
          };
        }

        if (options.includeChildren) {
          result.children = element.getAllElements().map((child: any) => 
            this.elementToObject(child, options)
          );
        }

        return result;
      }
    });

    // XML сериализатор
    this.serializers.set(SerializationFormat.XML, {
      format: SerializationFormat.XML,
      async serialize(element: ICSElement, options: SerializationOptions): Promise<string> {
        const data = this.elementToXml(element, options);
        return options.pretty ? this.formatXml(data) : data;
      },
      async deserialize(data: string | Buffer): Promise<any> {
        return this.parseXml(data.toString());
      },
      validate(data: string | Buffer): boolean {
        try {
          const xmlString = data.toString();
          // Простая проверка на валидный XML
          return xmlString.includes('<') && xmlString.includes('>') && 
                 xmlString.match(/<[^>]+>/g) !== null;
        } catch {
          return false;
        }
      },
      elementToXml(element: ICSElement, options: SerializationOptions): string {
        let xml = `<element id="${element.id}" name="${element.name}">`;
        
        // Данные элемента
        for (const [key, value] of element.data) {
          xml += `<data key="${key}">${this.escapeXml(String(value))}</data>`;
        }

        // Дочерние элементы
        if (options.includeChildren) {
          element.getAllElements().forEach((child: any) => {
            xml += this.elementToXml(child, options);
          });
        }

        xml += '</element>';
        return xml;
      },
      formatXml(xml: string): string {
        // Простое форматирование XML
        return xml.replace(/></g, '>\n<');
      },
      parseXml(xml: string): any {
        // Простой парсер XML (заглушка)
        const result: any = {};
        const elementMatch = xml.match(/<element[^>]*>/);
        if (elementMatch) {
          const idMatch = elementMatch[0].match(/id="([^"]*)"/);
          const nameMatch = elementMatch[0].match(/name="([^"]*)"/);
          if (idMatch) result.id = idMatch[1];
          if (nameMatch) result.name = nameMatch[1];
        }
        return result;
      },
      escapeXml(str: string): string {
        return str.replace(/[<>&'"]/g, (c) => {
          switch (c) {
            case '<': return '&lt;';
            case '>': return '&gt;';
            case '&': return '&amp;';
            case "'": return '&apos;';
            case '"': return '&quot;';
            default: return c;
          }
        });
      }
    });

    // YAML сериализатор
    this.serializers.set(SerializationFormat.YAML, {
      format: SerializationFormat.YAML,
      async serialize(element: ICSElement, options: SerializationOptions): Promise<string> {
        try {
          const data = this.elementToObject(element, options);
          return yaml.dump(data, { 
            indent: options.pretty ? 2 : 0,
            lineWidth: 120,
            noRefs: true,
            sortKeys: true
          });
        } catch (error) {
          console.error('Ошибка сериализации в YAML:', error);
          // Возвращаем fallback к простой реализации
          const data = this.elementToObject(element, options);
          return this.objectToYaml(data, options.pretty ? 2 : 0);
        }
      },
      async deserialize(data: string | Buffer): Promise<any> {
        return this.parseYaml(data.toString());
      },
      validate(data: string | Buffer): boolean {
        try {
          yaml.load(data.toString());
          return true;
        } catch {
          return false;
        }
      },
      elementToObject(element: ICSElement, options: SerializationOptions): any {
        // Используем встроенную сериализацию CSElement
        if (typeof (element as any).serialize === 'function') {
          return (element as any).serialize({
            includeChildren: options.includeChildren,
            includeData: true,
            includeMetadata: options.includeMetadata
          });
        }

        // Fallback для совместимости
        const result: any = {
          id: element.id,
          name: element.name,
          data: Array.from(element.data.entries()).reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
          }, {} as Record<string, any>)
        };

        if (options.includeMetadata) {
          result.metadata = {
            index: element.index,
            elementsCount: element.elementsCount(),
            ownersCount: element.ownersCount()
          };
        }

        if (options.includeChildren) {
          result.children = element.getAllElements().map((child: any) => 
            this.elementToObject(child, options)
          );
        }

        return result;
      },
      objectToYaml(obj: any, indent: number = 0): string {
        const spaces = ' '.repeat(indent);
        let yaml = '';

        for (const [key, value] of Object.entries(obj)) {
          yaml += `${spaces}${key}:`;
          if (typeof value === 'object' && value !== null) {
            yaml += '\n';
            yaml += this.objectToYaml(value, indent + 2);
          } else {
            yaml += ` ${value}\n`;
          }
        }

        return yaml;
      },
      parseYaml(yamlString: string): any {
        try {
          return yaml.load(yamlString);
        } catch (error) {
          console.error('Ошибка парсинга YAML:', error);
          // Возвращаем пустой объект в случае ошибки
          return {};
        }
      }
    });

    // CSV сериализатор
    this.serializers.set(SerializationFormat.CSV, {
      format: SerializationFormat.CSV,
      async serialize(element: ICSElement, _options: SerializationOptions): Promise<string> {
        return this.elementToCsv(element);
      },
      async deserialize(data: string | Buffer): Promise<any> {
        return this.parseCsv(data.toString());
      },
      validate(data: string | Buffer): boolean {
        try {
          const csvString = data.toString();
          const lines = csvString.split('\n');
          // Простая проверка: должно быть минимум 2 строки и запятые
          return lines.length >= 2 && lines[0].includes(',');
        } catch {
          return false;
        }
      },
      elementToCsv(element: ICSElement): string {
        const dataObj = Array.from(element.data.entries()).reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {} as Record<string, any>);
        const headers = ['id', 'name', 'data'];
        const values = [element.id, element.name, JSON.stringify(dataObj)];
        return `${headers.join(',')}\n${values.join(',')}`;
      },
      parseCsv(csv: string): any {
        const lines = csv.split('\n');
        if (lines.length < 2) return {};
        
        const headers = lines[0].split(',');
        const values = lines[1].split(',');
        
        const result: any = {};
        headers.forEach((header, index) => {
          result[header] = values[index];
        });
        
        return result;
      }
    });

    // Binary (MessagePack) сериализатор
    this.serializers.set(SerializationFormat.BINARY, {
      format: SerializationFormat.BINARY,
      async serialize(element: ICSElement, options: SerializationOptions): Promise<Buffer> {
        try {
          const data = this.elementToObject(element, options);
          const encoded = msgpack.encode(data);
          return Buffer.from(encoded);
        } catch (error) {
          console.error('Ошибка бинарной сериализации:', error);
          // Возвращаем пустой буфер в случае ошибки
          return Buffer.alloc(0);
        }
      },
      async deserialize(data: string | Buffer): Promise<any> {
        try {
          const buffer = typeof data === 'string' ? Buffer.from(data, 'base64') : data;
          return msgpack.decode(buffer);
        } catch (error) {
          console.error('Ошибка бинарной десериализации:', error);
          return {};
        }
      },
      validate(data: string | Buffer): boolean {
        try {
          const buffer = typeof data === 'string' ? Buffer.from(data, 'base64') : data;
          msgpack.decode(buffer);
          return true;
        } catch {
          return false;
        }
      },
      elementToObject(element: ICSElement, options: SerializationOptions): any {
        // Используем встроенную сериализацию CSElement
        if (typeof (element as any).serialize === 'function') {
          return (element as any).serialize({
            includeChildren: options.includeChildren,
            includeData: true,
            includeMetadata: options.includeMetadata
          });
        }

        // Fallback для совместимости
        const result: any = {
          id: element.id,
          name: element.name,
          data: Array.from(element.data.entries()).reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
          }, {} as Record<string, any>)
        };

        if (options.includeMetadata) {
          result.metadata = {
            index: element.index,
            elementsCount: element.elementsCount(),
            ownersCount: element.ownersCount()
          };
        }

        if (options.includeChildren) {
          result.children = element.getAllElements().map((child: any) => 
            this.elementToObject(child, options)
          );
        }

        return result;
      }
    });
  }

  /**
   * Генерация ключа кэша
   */
  private generateCacheKey(element: ICSElement, options: SerializationOptions): string {
    const optionsString = JSON.stringify(options);
    return `${element.id}_${this.hashString(optionsString)}`;
  }

  /**
   * Простое хэширование строки
   */
  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString();
  }

  /**
   * Кэширование результата
   */
  private cacheResult(key: string, result: SerializationResult): void {
    // Проверяем лимит кэша
    if (this.cache.size >= this.config.maxCacheSize) {
      // Удаляем самую старую запись
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }

    this.cache.set(key, result);
  }

  /**
   * Сжатие данных с использованием gzip
   */
  private async compress(data: string | Buffer): Promise<Buffer> {
    try {
      const input = typeof data === 'string' ? Buffer.from(data, 'utf8') : data;
      const level = Math.max(0, Math.min(9, this.config.compressionLevel)) as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
      const compressed = pako.gzip(input, { level });
      return Buffer.from(compressed);
    } catch (error) {
      console.error('Ошибка сжатия данных:', error);
      // Возвращаем исходные данные в случае ошибки
      return typeof data === 'string' ? Buffer.from(data, 'utf8') : data;
    }
  }

  /**
   * Распаковка данных с использованием gzip
   */
  private async decompress(data: string | Buffer): Promise<string | Buffer> {
    try {
      const input = typeof data === 'string' ? Buffer.from(data, 'utf8') : data;
      const decompressed = pako.ungzip(input);
      return Buffer.from(decompressed);
    } catch (error) {
      console.error('Ошибка распаковки данных:', error);
      // Возвращаем исходные данные в случае ошибки
      return data;
    }
  }

  /**
   * Шифрование данных с использованием AES
   */
  private async encrypt(data: string | Buffer): Promise<Buffer> {
    try {
      if (!this.config.encryptionKey) {
        throw new Error('Ключ шифрования не установлен');
      }

      const input = typeof data === 'string' ? data : data.toString('utf8');
      const encrypted = CryptoJS.AES.encrypt(input, this.config.encryptionKey).toString();
      return Buffer.from(encrypted, 'utf8');
    } catch (error) {
      console.error('Ошибка шифрования данных:', error);
      // Возвращаем исходные данные в случае ошибки
      return typeof data === 'string' ? Buffer.from(data, 'utf8') : data;
    }
  }

  /**
   * Расшифровка данных с использованием AES
   */
  private async decrypt(data: string | Buffer): Promise<string | Buffer> {
    try {
      if (!this.config.encryptionKey) {
        throw new Error('Ключ шифрования не установлен');
      }

      const input = typeof data === 'string' ? data : data.toString('utf8');
      const decrypted = CryptoJS.AES.decrypt(input, this.config.encryptionKey);
      const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
      
      if (!decryptedString) {
        throw new Error('Не удалось расшифровать данные');
      }

      return Buffer.from(decryptedString, 'utf8');
    } catch (error) {
      console.error('Ошибка расшифровки данных:', error);
      // Возвращаем исходные данные в случае ошибки
      return data;
    }
  }

  /**
   * Получение поддерживаемых форматов
   */
  getSupportedFormats(): SerializationFormat[] {
    return Array.from(this.serializers.keys());
  }

  /**
   * Проверка поддержки формата
   */
  supportsFormat(format: SerializationFormat): boolean {
    return this.serializers.has(format);
  }

  /**
   * Валидация данных для формата
   */
  validate(data: string | Buffer, format: SerializationFormat): boolean {
    const serializer = this.serializers.get(format);
    if (!serializer || !serializer.validate) {
      return false;
    }

    return serializer.validate(data);
  }

  /**
   * Очистка кэша
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Получение статистики кэша
   */
  getCacheStats(): { size: number; maxSize: number } {
    return {
      size: this.cache.size,
      maxSize: this.config.maxCacheSize
    };
  }

  /**
   * Обновление конфигурации
   */
  updateConfig(config: Partial<SerializationConfig>): void {
    this.config = { ...this.config, ...config };
    
    if (!this.config.enableCache) {
      this.clearCache();
    }
  }

  /**
   * Получение конфигурации
   */
  getConfig(): SerializationConfig {
    return { ...this.config };
  }
}

/**
 * Фабричная функция для создания плагина сериализации
 */
export function serializationPlugin(config?: Partial<SerializationConfig>): SerializationPlugin {
  return new SerializationPlugin({ config });
} 